import React, { memo, useCallback, useRef } from 'react';
import { pick } from 'ramda';
import { useForm } from 'react-final-form';
import { clsx } from 'js/utils/classNames.util';
import { useUserSelector } from 'js/store/hooks';
import { useEffectOnce } from 'js/hooks/useEffectOnce';
import { useComponentTranslations } from 'js/hooks/component';
import { Checkbox } from 'js/components/common/Fields/Checkbox';
import { AccordionRowNew } from 'js/components/common/AccordionRowNew';
import { TFormFieldContactPreferences } from 'js/components/common/Form/types';

import { useHandleSelectAllChange } from '../../hooks';
import { useGetUserValues, useDefaultValues } from '../../../../hooks';
import { SELECT_ALL_SUFFIX, CONTACT_PREFERENCES_FIELD_TYPE } from '../../../../constants';

import style from './Preference.scss';

export const PreferenceBare: React.FC<{
    channel?: string;
    field: TFormFieldContactPreferences;
    isProductsAvailable?: boolean;
    mainSelectAllFieldRef?: React.RefObject<HTMLInputElement>;
    options?: string[];
    prefFieldId: string;
    setRefsList?: React.Dispatch<React.SetStateAction<React.RefObject<HTMLInputElement>[]>>;
}> = ({
    channel,
    field,
    prefFieldId,
    isProductsAvailable = false,
    mainSelectAllFieldRef,
    options = [],
    setRefsList
}) => {
    const selectAllFieldRef = useRef<HTMLInputElement>(null);
    const formAPI = useForm();
    const translate = useComponentTranslations();
    const getUserValues = useGetUserValues({ playerTags: [] });
    const { userDetails, isLoggedIn } = useUserSelector(pick(['userDetails', 'isLoggedIn']));
    const defaultValues = useDefaultValues();
    const userValues = getUserValues({ fields: [field], userDetails });
    const { enableSelectAll, preferencesType } = field;
    const fieldName = channel ? `${prefFieldId}-${channel}` : prefFieldId;
    const handleSelectAllDirectChildrenItemChange = useHandleSelectAllChange(
        { selectAllFieldRef, mainSelectAllFieldRef, prefFieldId }
    );

    const updateSelectAllItems = useCallback(({ isUpdateOnLoad = false }: { isUpdateOnLoad?: boolean }) => {

        if (!enableSelectAll && (preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.NON_PROMOTIONAL
            || preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.PROMOTIONAL && !isProductsAvailable)) {
            return;
        }

        const ref = selectAllFieldRef.current ? selectAllFieldRef : mainSelectAllFieldRef;
        const combinedUserValues = { ...defaultValues, ...userValues };
        const values = isUpdateOnLoad ? combinedUserValues : formAPI.getState().values;
        const siblingFields = formAPI.getRegisteredFields()
            .filter(item => item.includes(fieldName) && !item.includes(SELECT_ALL_SUFFIX));
        const checkedSiblingFieldsCount = siblingFields.filter(item => values[item]).length;

        if (ref && ref.current) {
            // set appropriate value to "Select All" parent field in case if products do not available to this channel
            if (checkedSiblingFieldsCount === siblingFields.length) {
                ref.current.indeterminate = false;
                formAPI.change(`${fieldName}${SELECT_ALL_SUFFIX}`, true);
            } else if (checkedSiblingFieldsCount === 0) {
                ref.current.indeterminate = false;
                formAPI.change(`${fieldName}${SELECT_ALL_SUFFIX}`, false);
            } else {
                ref.current.indeterminate = true;
            }
        }

        // set appropriate value to top level "Select All" field in case if some products available
        if (mainSelectAllFieldRef && mainSelectAllFieldRef.current) {
            const updatedValues = isUpdateOnLoad ? combinedUserValues : formAPI.getState().values;
            const selectAllField = `${prefFieldId}${SELECT_ALL_SUFFIX}`;
            const allFields = formAPI.getRegisteredFields()
                .filter(item => item.includes(prefFieldId) && !item.includes(selectAllField));

            if (allFields.every(item => updatedValues[item])) {
                mainSelectAllFieldRef.current.indeterminate = false;
                formAPI.change(selectAllField, true);
            } else if (allFields.every(item => !updatedValues[item])) {
                mainSelectAllFieldRef.current.indeterminate = false;
                formAPI.change(selectAllField, false);
            } else {
                mainSelectAllFieldRef.current.indeterminate = true;
            }
        }
    }, [
        defaultValues,
        enableSelectAll,
        fieldName,
        formAPI,
        isProductsAvailable,
        mainSelectAllFieldRef,
        preferencesType,
        prefFieldId,
        userValues
    ]);

    useEffectOnce(() => {
        setRefsList && setRefsList((prev) => [...prev, selectAllFieldRef]);

        if (isLoggedIn) {
            updateSelectAllItems({ isUpdateOnLoad: true });
        }
    }, [isLoggedIn, updateSelectAllItems]);

    if (preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.PROMOTIONAL && isProductsAvailable) {
        return (
            <>
                {Boolean(options.length) && (
                    <AccordionRowNew
                        unmountOnExit
                        classNames={{
                            accordionContainer: style.accordionContainer,
                            accordionHeaderArrowRow: style.accordionHeaderArrowRow
                        }}
                        id={`${fieldName}${SELECT_ALL_SUFFIX}`}
                        headerCells={[
                            {
                                id: `${fieldName}${SELECT_ALL_SUFFIX}`,
                                content: (
                                    <div className={`${style.singleCheckboxContainer} ${style.accordionHeader}`}>
                                        <Checkbox
                                            aria-label={translate(`playtech.mobile.system.form.contactPreferences.${preferencesType}.${channel}`)}
                                            id={`${fieldName}${SELECT_ALL_SUFFIX}`}
                                            itemRef={selectAllFieldRef}
                                            label={translate(`playtech.mobile.system.form.contactPreferences.${preferencesType}.${channel}`)}
                                            name={`${fieldName}${SELECT_ALL_SUFFIX}`}
                                            onChange={handleSelectAllDirectChildrenItemChange}
                                            onClick={(event: React.MouseEvent) => event.stopPropagation()}
                                        />
                                    </div>
                                )
                            }
                        ]}
                        bodyCells={[
                            {
                                id: `${fieldName}-${channel}`,
                                className: style.productsContainer,
                                content: (
                                    <>
                                        <span className={style.productText}>{translate('playtech.mobile.system.form.contactPreferences.promotional.products')}</span>
                                        {options.map((option) => (
                                            <div key={`${fieldName}-${option}`}>
                                                <Checkbox
                                                    aria-label={translate(`playtech.mobile.system.form.contactPreferences.${preferencesType}.${option}`)}
                                                    className={style.productItem}
                                                    id={`${fieldName}-${option}`}
                                                    label={`playtech.mobile.system.form.contactPreferences.${preferencesType}.${option}`}
                                                    name={`${fieldName}-${option}`}
                                                    onChange={updateSelectAllItems}
                                                />
                                            </div>
                                        ))}
                                    </>
                                )
                            }
                        ]}
                    />
                )}
                {Boolean(!options.length) && (
                    <div className={style.singleCheckboxContainer}>
                        <Checkbox
                            aria-label={translate(`playtech.mobile.system.form.contactPreferences.${preferencesType}.${channel}`)}
                            id={fieldName}
                            label={translate(`playtech.mobile.system.form.contactPreferences.${preferencesType}.${channel}`)}
                            name={fieldName}
                            onChange={updateSelectAllItems}
                        />
                    </div>
                )}
            </>
        );
    }

    if (preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.NON_PROMOTIONAL
        || preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.PROMOTIONAL && !isProductsAvailable) {
        return (
            <>
                {Boolean(options.length) && enableSelectAll && (
                    <div
                        className={style.singleCheckboxContainer}
                        key={`${fieldName}${SELECT_ALL_SUFFIX}`}
                    >
                        <Checkbox
                            aria-label={translate('playtech.mobile.system.form.contactPreferences.selectAll.label')}
                            id={`${fieldName}${SELECT_ALL_SUFFIX}`}
                            itemRef={selectAllFieldRef}
                            label={translate('playtech.mobile.system.form.contactPreferences.selectAll.label')}
                            name={`${fieldName}${SELECT_ALL_SUFFIX}`}
                            onChange={handleSelectAllDirectChildrenItemChange}
                        />
                    </div>
                )}
                {Boolean(options.length) && (
                    <div>{
                        options.map(option => (
                            <div
                                className={style.singleCheckboxContainer}
                                key={`${fieldName}-${option}`}
                            >
                                <Checkbox
                                    aria-label={translate(`playtech.mobile.system.form.contactPreferences.${preferencesType}.${option}`)}
                                    className={clsx([channel && style.productItem])}
                                    id={`${fieldName}-${option}`}
                                    label={`playtech.mobile.system.form.contactPreferences.${preferencesType}.${option}`}
                                    name={`${fieldName}-${option}`}
                                    onChange={updateSelectAllItems}
                                />
                            </div>
                        ))
                    }</div>
                )}
            </>
        );
    }

    return null;
};

export const Preference = memo(PreferenceBare);
